import infoIcon from 'assets/icons/info-blue.svg';
import addIcon from 'assets/icons/plus-deep-blue.svg';
import { AccountsContext } from 'context/accounts/AccountsContext';
import { ApplicationsContext } from 'context/applications/ApplicationsContext';
import { ApplicationsContextProvider } from 'context/applications/ApplicationsContextProvider';
import { RolesContext } from 'context/roles/RolesContext';
import React, { Fragment, useCallback, useContext, useState } from 'react';
import { Redirect } from 'react-router';
import { Column, Link, Loader, LocaleContext, PageHeader, Row, Tooltip } from 'shared';
import { useQuery } from 'shared/hooks';
import { Account, OWNER_ROLE } from 'store/api/types';

import { AddApplication } from './addApplication/AddApplication';
import { ApplicationBox } from './applicationBox/ApplicationBox';
import {
  AddApplicationButton,
  ApplicationPlaceholder,
  ApplicationsWrapper,
  PageHeaderInfo,
  TooltipContent,
} from './Applications.styles';
import { ApplicationsProps } from './Applications.types';

export const Applications = ({
  onAddApplication,
  onFetchApplications,
  match: {
    params: { accountId },
  },
}: ApplicationsProps) => {
  const accountsContext = useContext(AccountsContext);
  const localeContext = useContext(LocaleContext);
  const rolesContext = useContext(RolesContext);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const fetchApplications = useCallback(() => onFetchApplications(accountId), [accountId, onFetchApplications]);
  const { response, error, loading } = useQuery(fetchApplications);
  const openAddModal = () => setAddModalVisible(true);
  const closeAddModal = () => setAddModalVisible(false);
  const activeAccount = accountsContext.activeAccount as Account;
  const hasOwnership = activeAccount && rolesContext.getRoleInAccount(activeAccount.uuid) === OWNER_ROLE;

  const renderHeaderActions = () => {
    if (hasOwnership) {
      return (
        <AddApplicationButton onClick={openAddModal} className="applications__add-app">
          {localeContext.formatMessage({ id: 'DASHBOARD.CREATE-NEW-APP' })}
          <img src={addIcon} alt={localeContext.formatMessage({ id: 'DASHBOARD.CREATE-NEW-APP' })} />
        </AddApplicationButton>
      );
    }

    return (
      <Tooltip
        placement="bottom"
        content={<TooltipContent>{localeContext.formatMessage({ id: 'DASHBOARD.SANDBOX-INFO' })}</TooltipContent>}
      >
        {({ ref }) => (
          <PageHeaderInfo className="applications__header-info" ref={ref}>
            {localeContext.formatMessage({ id: 'DASHBOARD.ONLY-1-TEST-APP-AVAILABLE' })}
            <img src={infoIcon} alt="Sandbox" />
          </PageHeaderInfo>
        )}
      </Tooltip>
    );
  };

  return (
    <Fragment>
      {error && <Redirect to="/logout" />}

      {loading && <Loader />}

      {response && (
        <ApplicationsContextProvider applications={response.payload}>
          <ApplicationsWrapper className="applications">
            <PageHeader actions={renderHeaderActions()}>
              {localeContext.formatMessage({ id: 'DASHBOARD.APPLICATIONS' })}
            </PageHeader>

            <Row className="applications__row">
              <ApplicationsContext.Consumer>
                {({ applications }) =>
                  applications.map(application => (
                    <Column className="applications__column" size={[12, 12, 6]} key={application.uuid}>
                      <Link
                        url={`/${activeAccount.uuid}/dashboard/${application.uuid}`}
                        key={application.uuid}
                        className="link--account"
                      >
                        <ApplicationBox {...application} />
                      </Link>
                    </Column>
                  ))
                }
              </ApplicationsContext.Consumer>
              {hasOwnership && (
                <Column className="applications__column" size={[12, 12, 6]} key="new">
                  <ApplicationPlaceholder onClick={openAddModal} className="application application--new">
                    <img src={addIcon} alt={localeContext.formatMessage({ id: 'DASHBOARD.CREATE-NEW-APP' })} />
                    {localeContext.formatMessage({ id: 'DASHBOARD.CREATE-NEW-APP' })}
                  </ApplicationPlaceholder>
                </Column>
              )}
            </Row>

            <AddApplication isOpen={addModalVisible} onClose={closeAddModal} onAddApplication={onAddApplication} />
          </ApplicationsWrapper>
        </ApplicationsContextProvider>
      )}
    </Fragment>
  );
};
